//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import { InvisibleButton } from '@components/atoms/button/Button';
import HR from '@components/atoms/hr/HR';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodyText, CaptionSecondaryText } from '@components/atoms/text/Text';
import { CheckboxSetFilter } from '@components/media-filters/CheckboxSetFilter';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { DropdownFilter } from '@components/media-filters/DropdownFilter';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { AllProductsModal } from '@content-drawer/filters/products/AllProductsModal';
import { ProductsTypeaheadSearch } from '@content-drawer/filters/products/ProductTypeaheadSearch';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { getIsContentLibraryProductsImprovementsEnabled } from '@media-library/redux/getMediaLibraryPage';
import { isUndefined } from '@utils/filterPredicates';

export const SELECTED_PRODUCTS_LIMIT = 10;
export const DEFAULT_PRODUCT_TAGS_FILTER_SECTION: FilterSection<boolean> = {
  options: [
    { isSelected: false, label: 'Has product tags', value: true },
    { isSelected: false, label: "Doesn't have product tags", value: false },
  ],
  paramKey: 'hasProducts',
};

export const DEFAULT_HAS_PRODUCTS_FILTER_SECTION: FilterSection<boolean> = {
  options: [
    {
      isSelected: false,
      label: 'No products tagged',
      trackingId: MediaLibraryTrackingIds.PRODUCTS_NOT_TAGGED_CHECKBOX,
      value: false,
    },
    {
      isSelected: false,
      label: 'Product tagged',
      trackingId: MediaLibraryTrackingIds.PRODUCTS_TAGGED_CHECKBOX,
      value: true,
    },
  ],
  paramKey: 'hasProducts',
};

export const DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'productIds',
};

export interface ProductTagsFilterProps {
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  section: FilterSection<boolean> | undefined;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
}

export const ProductTagsFilter = ({
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section,
  sections,
}: ProductTagsFilterProps) => {
  // TODO: PD-297467 - Remove content_library_products_improvements feature flag
  const isContentLibraryProductsImprovementsEnabled = useSelector(
    getIsContentLibraryProductsImprovementsEnabled,
  );

  const initialSection =
    section ??
    (isContentLibraryProductsImprovementsEnabled
      ? DEFAULT_HAS_PRODUCTS_FILTER_SECTION
      : DEFAULT_PRODUCT_TAGS_FILTER_SECTION);

  const subSection: FilterSection<string> =
    getSection(sections, 'productIds') ??
    DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const selectedProducts = subSection.options
    .filter((option) => option.isSelected)
    .map((option) => ({
      id: option.value,
      image: undefined, // TODO: PD-298529 - replace with real image URL
      name: option.label,
    }));

  const updateSubSection = (
    options: FilterOption<FilterValue>[],
    shouldUpdateParent: boolean = false,
  ) => {
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    updatedSections.set(subSection.paramKey, {
      ...subSection,
      options,
    });

    if (shouldUpdateParent) {
      const parentOptions = initialSection.options.map((option) => ({
        ...option,
        isSelected: !!option.value,
      }));

      updatedSections.set(initialSection.paramKey, {
        ...initialSection,
        options: parentOptions,
      });
    }

    onChange(updatedSections);
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={
          isContentLibraryProductsImprovementsEnabled
            ? FilterSectionTitle[subSection.paramKey]
            : FilterSectionTitle[initialSection.paramKey] // TODO: PD-297467 - Update FilterSectionTitle when feature flag GA'd
        }
      >
        {isContentLibraryProductsImprovementsEnabled ? (
          <>
            <CheckboxSetFilter
              isDisabled={isDisabled}
              isSingleSelect={true}
              onChange={onChange}
              section={initialSection}
              subsection={subSection}
            />
            <ProductsSubsection>
              <ProductsTypeaheadSearch
                openModal={() => setIsOpen(true)}
                searchTerm={searchTerm}
                selectProduct={(selectedProduct) => {
                  if (selectedProducts.length < SELECTED_PRODUCTS_LIMIT) {
                    const selectedOption = subSection.options.find(
                      (option) => option.value === selectedProduct.value,
                    );

                    if (isUndefined(selectedOption)) {
                      updateSubSection(
                        [...subSection.options, selectedProduct],
                        true,
                      );
                    } else {
                      updateSubSection(
                        subSection.options.map((option) => {
                          if (option.value === selectedProduct.value) {
                            return {
                              ...option,
                              isSelected: true,
                            };
                          }
                          return option;
                        }),
                        true,
                      );
                    }
                  }
                }}
                setSearchTerm={setSearchTerm}
              />
              <ClearSelectionButton
                handleClearSection={updateSubSection}
                options={subSection.options}
                trackingId={
                  MediaLibraryTrackingIds.PRODUCTS_CLEAR_SELECTION_BUTTON
                }
              />
              {selectedProducts
                .slice(0, SELECTED_PRODUCTS_LIMIT)
                .map((product) => (
                  <ProductContainer key={product.id}>
                    <ProductContents>
                      <StyledImage alt={product.name} src={product.image} />
                      <div>
                        <BodyText ellipsize={1} title={product.name}>
                          {product.name}
                        </BodyText>
                        <CaptionSecondaryText ellipsize={1} title={product.id}>
                          {product.id}
                        </CaptionSecondaryText>
                      </div>
                    </ProductContents>
                    <RemoveProductButton
                      aria-label='Remove product'
                      iconName='close'
                      onClick={() => {
                        updateSubSection(
                          subSection.options.map((option) => {
                            if (option.value === product.id) {
                              return {
                                ...option,
                                isSelected: false,
                              };
                            }
                            return option;
                          }),
                        );
                      }}
                      trackingId={
                        MediaLibraryTrackingIds.PRODUCTS_REMOVE_PRODUCT_BUTTON
                      }
                      type='button'
                    />
                  </ProductContainer>
                ))}
              {isOpen && (
                <AllProductsModal
                  initialSearchTerm={searchTerm}
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  options={subSection.options}
                  updateSelectedProducts={(selectedProducts) =>
                    updateSubSection(selectedProducts, true)
                  }
                />
              )}
            </ProductsSubsection>
          </>
        ) : (
          <DropdownFilter
            isDisabled={isDisabled}
            onChange={(section: FilterSection<FilterValue>) => {
              const updatedSections = new Map<
                MediaFilterParamKey,
                FilterSection<FilterValue>
              >();

              updatedSections.set(section.paramKey, section);
              onChange(updatedSections);
            }}
            placeholder='Not specified'
            section={initialSection}
          />
        )}
      </CollapsibleSection>

      <HR />
    </>
  );
};

const ProductsSubsection = styled.div(
  ({ theme }) => `
    padding-left: ${theme.spacings[4]};
  `,
);

const StyledImage = styled(ImageWithErrorHandling)(
  ({ theme }) => `
    width: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    height: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    object-fit: contain;
  `,
);

const ProductContainer = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
    justify-content: space-between;
    padding-bottom: ${theme.spacings[1]};
  `,
);

const RemoveProductButton = styled(InvisibleButton)(
  ({ theme }) => `
    width: ${theme.spacings[3]};
    height: ${theme.spacings[3]};
  `,
);

const ProductContents = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
  `,
);
